import React, { FC, useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import { LanguageContext } from "common/contexts/LanguageContext";
import useProjectStore, { DistanceUnit, IObstructionInstance } from "common/store/ProjectStore";
import DialogTextField, { DialogNumberField } from "common/components/dialogs/DialogTextField";
import DialogDropdownTextField from "common/components/dialogs/DialogDropdownTextField";
import DialogDropdownMenuItem from "common/components/dialogs/DialogDropdownMenuItem";
import useNotificationStore from "common/store/NotificationStore";
import { DecimalToUndredthsRegEx, ValidInputCharsRegEx } from "common/constants/GlobalValues";
import { formatNumber } from "common/utils/formatting";

interface IProps {
  instanceID: number;
  isOpen: boolean;
  setIsOpen: (b: boolean) => void;
}

const ObstructionProperties: FC<IProps> = ({ instanceID, isOpen, setIsOpen }) => {
  const [origInstance, setOrigInstance] = useState<IObstructionInstance | null>(null);
  const [name, setName] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [width, setWidth] = useState<string>("");
  const [length, setLength] = useState<string>("");
  const [distanceAbbr, setDistanceAbbr] = useState<string>("");

  const { obstructionInstances, updateObstructionInstance, distanceID } = useProjectStore();
  const { enqueueNotification } = useNotificationStore();
  const { getString } = useContext(LanguageContext);

  useEffect(() => {
    var origInstance = obstructionInstances.find((inst) => inst.obstructionInstanceID === instanceID);
    if (origInstance !== undefined) {
      setOrigInstance(origInstance);
      setName(origInstance.obstructionName);
      setHeight(formatNumber(origInstance.height || 0));
      setWidth(formatNumber(origInstance.width || 0));
      setLength(formatNumber(origInstance.length || 0));
    }
    setOrigInstance(origInstance !== undefined ? { ...origInstance } : null);
    setDistanceAbbr(getString(distanceID === DistanceUnit.Feet ? "cmn_feet_abbr" : "cmn_meters_abbr"));
  }, [obstructionInstances, instanceID, distanceID, getString]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleConfirm = () => {
    if (name && !ValidInputCharsRegEx.test(name)){
      enqueueNotification("invalid_chars", `${getString('msg_invalid_chars')} ${getString('ObstructionName')}`, "error");
      return;    
    }

    if (origInstance) {
      updateObstructionInstance(instanceID, {
        ...origInstance,
        obstructionInstanceID: instanceID,
        obstructionName: name,
        height: +height,
        width: +width,
        length: +length,
      });
    }
    setIsOpen(false);
  };

  const handleNumberChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setValue: (s: string) => void,
  ) => {
    const inputValue = event.target.value;
    // Allow only numeric values
    if (DecimalToUndredthsRegEx.test(inputValue)) {
      setValue(inputValue);
    }
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      title={getString("dlg_obsprops_title")}
      maxWidth="sm"
      hideDivider={true}
    >
      <Box sx={{ m: "20px", p: "20px" }}>
        <DialogTextField
          variant="filled"
          size="small"
          label={getString("dlg_obsprops_name_lbl")}
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <DialogNumberField
          sx={{ mt: "24px" }}
          label={`${getString("dlg_obsprops_ht_lbl")} (${distanceAbbr})`}
          type="text"
          variant="filled"
          size="small"
          fullWidth
          value={height}
          onChange={(e) => handleNumberChange(e, setHeight)}
        />
        <DialogNumberField
          sx={{ mt: "24px" }}
          label={`${getString("dlg_obsprops_wdt_lbl")} (${distanceAbbr})`}
          type="text"
          variant="filled"
          size="small"
          fullWidth
          value={width}
          onChange={(e) => handleNumberChange(e, setWidth)}
        />
        <DialogNumberField
          sx={{ mt: "24px" }}
          label={`${getString("dlg_obsprops_len_lbl")} (${distanceAbbr})`}
          type="text"
          variant="filled"
          size="small"
          fullWidth
          value={length}
          onChange={(e) => handleNumberChange(e, setLength)}
        />
        <DialogDropdownTextField
          label={getString("dlg_obsprops_obsfct_lbl")}
          sx={{ mt: "24px" }}
          select
          variant="outlined"
          size="small"
          fullWidth
          value={0}
        >
          <DialogDropdownMenuItem key={0} value={0}>
            {getString('dlg_obsprops_ttl_objs')}
          </DialogDropdownMenuItem>
        </DialogDropdownTextField>
      </Box>
    </ConfirmationModal>
  );
};

export default ObstructionProperties;
